// React
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';

import { breadcrumbsActions } from '@store/breadcrumbs';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import { usePermissions } from '@store/auth';
import {
  getLOBs,
  getLOBDetails,
  paymentOptionsMerchantsActions,
  selectLOBList,
} from '../../store/payment-options-merchants';
import { getPlatforms } from '../../store/payment-options-platforms';

import {
  LOB_ADD_ROUTE,
  LOB_LIST_ROUTE,
  viewLOBs,
} from '../../payment-options.breadcrumbs';
import TableToolbar from '../../../../components/TableToolbar';

function MerchantsToolbar({ navigate, permissions }) {
  return (
    <TableToolbar>
      <Button
        disabled={!permissions?.canEditPaymentOptions}
        variant="contained"
        size="large"
        onClick={() => navigate(LOB_ADD_ROUTE)}
        startIcon={<AddIcon />}>
        Add new LOB
      </Button>
    </TableToolbar>
  );
}

function PaymentMerchants() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = usePermissions();

  const [isFetchingData, setIsFetchingData] = useState(true);
  const lobs = useSelector(selectLOBList);

  // eslint-disable-next-line no-unused-vars -- remove once invoker is uncommented
  function openMerchantDetailsHandler(id) {
    setIsFetchingData(true);
    dispatch(getLOBDetails(id)).then(() => {
      setIsFetchingData(false);
    });
  }

  // @TODO: can be transferred to a routing service
  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewLOBs));
    dispatch(paymentOptionsMerchantsActions.resetSelectedMerchant());
  }, [dispatch]);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getPlatforms());
    dispatch(getLOBs()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  function getModifiedDetails({ row }) {
    return moment(row.modifiedAt).format('YYYY/MM/DD - HH:mm');
  }

  return (
    <ApplicationPageTemplate
      title="Lines of Business"
      description="List of the defined LOBs in the system. You can use the page to add and update payment merchants."
      pageReady={!isFetchingData}>
      <DataGrid
        density="standard"
        sx={{ background: 'white' }}
        rows={lobs}
        rowsPerPageOptions={[10]}
        pageSize={10}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        components={{
          Toolbar: () => MerchantsToolbar({ navigate, permissions }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => navigate(`${LOB_LIST_ROUTE}/${row.lobId}`)}
        getRowId={(row) => row.lobId}
        columns={[
          {
            field: 'lobId',
            headerName: 'ID',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'lobName',
            headerName: 'Name',
            flex: 1,
            sortable: false,
            headerAlign: 'left',
          },
          {
            field: 'lobCode',
            headerName: 'OMS Order Type',
            flex: 1,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedBy',
            headerName: 'Last Modified By',
            flex: 1,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedAt',
            headerName: 'Last Modified Date',
            flex: 1,
            sortable: false,
            headerAlign: 'right',
            align: 'right',
            valueGetter: getModifiedDetails,
          },
          // {
          //   field: 'details',
          //   type: 'actions',
          //   headerName: 'Actions',
          //   renderCell: ({ row }) => (
          //     <div>
          //       <ButtonGroup
          //         variant="outlined"
          //         aria-label="outlined button group">
          //         <Button
          //           startIcon={<AssignmentIcon />}
          //           variant="outlined"
          //           onClick={() => openMerchantDetailsHandler(row.lobId)}>
          //           Methods
          //         </Button>
          //       </ButtonGroup>
          //     </div>
          //   ),
          //   flex: 1,
          // },
        ]}
      />
    </ApplicationPageTemplate>
  );
}

export default PaymentMerchants;
